/**
 * The two `catalogStore` fields whose SHAPE or EXISTENCE differs between the POS apps.
 *
 * `catalog/index.ts` is the domain's only store reader and stays platform-agnostic; these are
 * pure projections of the slice it hands in. Ledger hazard 38, errors 2 and 3.
 *
 * | field | web | mobile |
 * |---|---|---|
 * | `optionSetData` | the raw ARRAY (`getOptionSetsDataSuccess` stores `response`) | a `mid`-keyed MAP (`arrayToMap(response, "mid")`) |
 * | `mostSoldItems` | fetched at login and stored | **does not exist** — no field, no reducer, no saga, no endpoint |
 *
 * ✅ **`optionSetData` no longer needs the split, and as of 2026-09-09 does not have one** —
 * `optionSetsOf` below is shared. Both shapes hold the same option-set objects, so ONE reader
 * covers them, and the folder's variants are down to `mostSoldItemIdsOf`.
 *
 * The earlier note here argued a runtime shape check was worth avoiding because the folder had
 * to exist for `mostSoldItems` anyway. That reasoning does not survive contact with the rest of
 * the domain: `ordering/cart/selectors.ts`'s `selectOptionSetsById` already resolves this exact
 * divergence with this exact `Array.isArray` check, in a plain shared `.ts` with no split. Two
 * answers to one question is the drift a split is supposed to prevent, not an instance of it.
 *
 * 🔴 **`mostSoldItems` still forces the split, and is NOT convergeable.** Mobile has no producer
 * at any layer: `getMostSoldItems` / `saveMostSoldItems` / `mostSoldItemsCallComplete`, the
 * `fetchMostSoldItems` worker, its watcher, `ENDPOINT.POS.getMostSoldItems`
 * (`analytics/item/frequent`) and the `login/fetchCatalog` orchestration are ALL `.desktop`-only
 * — `grep -ri mostsold apps/mobile/src` is empty. The field is absent from mobile's
 * `CatalogState`, so a shared reader is a compile error there and only a cast would silence it.
 * Converging means adding a login-time API call to the mobile POS — a feature port, not a
 * typecheck fix, and one that changes cashier boot behaviour on a flow the kiosk is not meant to
 * touch (KIOSK_ARCHITECTURE §13.9: track A builds new modules, it does not repoint the POS). So
 * the native reader returns `[]` and the kiosk's Recommended row shows chef-recommended items
 * only on mobile — a known, documented gap rather than a silent one.
 */

/** Just enough of `CatalogState` to read the field, so this stays one function under both programs. */
interface CatalogFieldSource {
  optionSetData?: any[] | Record<string, any>;
  /** Web-only in practice; declared optional so the READ compiles under both programs. */
  mostSoldItems?: any[];
}

/**
 * The channel's option sets, as the array the catalog library and the customiser want.
 *
 * ⚠️ **Web's value is returned AS-IS — same reference, not a copy.** That is the whole of web's
 * previous body (`optionSetData ?? []`) preserved exactly, so nothing on that platform changed
 * when this collapsed. Only mobile's `mid`-keyed map pays for a flatten, with the same
 * `Object.values` its own `parseOptionSets` opens with.
 *
 * The asymmetry is deliberate rather than cosmetic: `Object.values` builds a NEW array per call,
 * which a `useSelector` would treat as a fresh value on every store action. Web keeping its
 * reference means only mobile carries that, and mobile already did. The reactive path avoids the
 * question entirely — `catalog/hooks/useCatalogSources` selects `selectRawOptionSetData`, for
 * exactly this reason (`catalog/prepared.ts`); `selectOptionSets` is read imperatively only.
 *
 * ⚠️ Readers only. `prepareOptionMapForChannel` WRITES through `selectRawOptionSetData` instead,
 * because each platform's `getOptionMapData` reducer expects its own shape.
 */
export function optionSetsOf(catalogStore: CatalogFieldSource | undefined): any[] {
  const optionSetData = catalogStore?.optionSetData;
  if (Array.isArray(optionSetData)) return optionSetData;
  return Object.values(optionSetData ?? {});
}

/** An entry may be an id or an object carrying one; the POS's reducer stores whatever the API sent. */
export function mostSoldItemIdOf(entry: any): string {
  return entry?.itemId ?? entry;
}

/**
 * Normalise a stored most-sold list to bare item ids.
 *
 * ⚠️ **Builds a NEW ARRAY per call, so it must not be the thing a `useSelector` returns.** It
 * would then be reference-unequal on every store action and re-render its component on every
 * dispatch — which is exactly what `useRecommendedGroups` did until 2026-09-14, and through it
 * every consumer of `useCategoryCatalog`. The reactive path selects the RAW field
 * (`selectRawMostSoldItems` -> `rawMostSoldItemsOf`, platform-split beside `mostSoldItemIdsOf`)
 * and calls this inside its own `useMemo` instead. `optionSetsOf` above carries the same warning
 * and `useCatalogSources` the same fix; this is the second instance of one hazard, not a new one.
 */
export function mostSoldItemIds(entries: any[] | undefined): string[] {
  return (entries ?? []).map(mostSoldItemIdOf);
}

/**
 * The stored field, untouched — what a `useSelector` may safely return.
 *
 * SHARED, unlike `mostSoldItemIdsOf` beside it, and the asymmetry is deliberate rather than an
 * oversight. That one is split because its NATIVE answer is a deliberate `[]` — the documented
 * gap above — while this one only has to hand back whatever is stored, which on mobile is
 * `undefined` because the field does not exist. `CatalogFieldSource` is structural, so reading it
 * compiles under both programs without a cast, exactly as `optionSetsOf` does; a `.native`
 * variant would add a second file and an unused parameter to return the same value.
 */
export function rawMostSoldItemsOf(catalogStore: CatalogFieldSource | undefined): any[] | undefined {
  return catalogStore?.mostSoldItems;
}
